import React, { useEffect, useState } from 'react';
import { Row, Col, Button, Divider, Grid, Image, Space } from 'antd';
import styled from 'styled-components';
import getConfig from 'next/config';
import { useRouter } from 'next/router';
import Tags from 'components/Tags';
import { HalfPage, Rectangle, SkyscraperMobile, Wideboard } from 'components/Ads';
import Photo from './Photo';
import GalleriesCarousel from 'components/GalleriesCarousel';
import VideoCard from 'components/VideoCard';
import FormattedDate from 'components/FormattedDate';
import Content from 'layout/Basic/Content';
import Sider from 'layout/Basic/Sider';
import Section, { SectionTitle } from 'components/Section';
import Newsletter from 'containers/Newsletter';
import Subscription from 'containers/Subscription';
import CompanyCarousel from 'components/CompanyCarousel';
import * as gtag from 'lib/gtag';
import ShareButtons from 'components/ShareButtons';
import resolveImagePath from 'lib/resolveImagePath';
import { merriweather } from 'styles/GlobalStyle';
import Link from 'next/link';
import { LeftCircleOutlinedIcon, RightCircleOutlinedIcon } from 'components/Icons';
import {
    ArrowLeftOutlined,
    ArrowRightOutlined,
    ZoomInOutlined,
    ZoomOutOutlined
} from '@ant-design/icons';

const ButtonGroup = Button.Group;

const PaginationButton = styled(Link).attrs<{ $disabled?: boolean }>(({ $disabled }) => {
    if ($disabled) {
        return {
            tabIndex: -1
        };
    }
    return {};
})`
    font-size: 28px;
    color: ${({ theme, $disabled }) =>
        $disabled ? theme.colors.grey : theme.colors.primary}!important;
    pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};
    padding: 0 5px;
`;

const PhotoButton = styled(Link).attrs<{ $disabled?: boolean }>(({ $disabled }) => {
    if ($disabled) {
        return {
            tabIndex: -1
        };
    }
    return {};
})`
    color: ${({ $disabled }) => ($disabled ? 'grey' : 'inherit')}!important;
    pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};
`;

const StyledDivider = styled(Divider)`
    color: ${(props) => props.theme.colors.primary};
    border-top-color: ${(props) => props.theme.colors.primary}!important;
`;

const { useBreakpoint } = Grid;

const SliderImage = styled(Image)`
    display: block;
    max-width: 100%;
    width: auto;
    height: auto;
    margin: 0 auto;
    object-fit: contain;
`;

const Description = styled.div`
    font-size: 23px;
    font-weight: 300;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.52;
    letter-spacing: normal;
    text-align: left;
    padding: 20px;
    overflow: hidden;
    color: ${({ theme }) => theme.colors.brownishGrey};
    @media (max-width: 768px) {
        padding: 0;
    }
`;

const Date = styled.div`
    color: ${(props) => props.color || props.theme.colors.greyishBrown};
    font-weight: 300;
    font-family: ${merriweather.style.fontFamily};
    line-height: 18px;
`;

const Title = styled.h1`
    font-size: 32px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.41;
    letter-spacing: normal;
    text-align: left;
    color: #707070;
`;

const StyledSection = styled(Section)`
    @media (max-width: 768px) {
        margin-top: 0;
    }
`;

const ToolbarWrapper = styled.div`
    border-radius: 100px;
    background: ${({ theme }) => theme.colors.primaryBackground};
    padding: 10px;
`;

export interface Props {
    gallery: Gallery;
    article: Article;
    contentRows: string[];
    category: Category[];
    events: Occurrence[];
    eventsPatronate: Company[];
    latestIssue: Issue;
    seeAlsoArticles: Article[];
    singleGallery: Gallery;
    singleVideo: Video;
    topOneInCampaign: {
        articles: Article[];
        campaign: Campaign[];
    };
    topInCategory: Article[];
    whitePapers: WhitePaper[];
    image: number;
    galleries: Gallery[];
    ads: PageAds;
}

const GalleryDetails = ({
    gallery,
    image = 0,
    galleries,
    singleVideo,
    latestIssue,
    ads
}: Props): JSX.Element => {
    const router = useRouter();
    const [previewVisible, setPreviewVisible] = useState(false);

    const sendShowPhotoEvent = () => {
        gtag.event({
            action: 'show-photo',
            category: 'Gallery',
            label: gallery.title,
            value: router.asPath
        });
    };

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'ArrowLeft' && image !== 0) {
                router.push(`/galerie/${gallery.seoSlug},${image - 1}#image`);
            }

            if (event.key === 'ArrowRight' && image + 1 < gallery.images.length) {
                router.push(`/galerie/${gallery.seoSlug},${image + 1}#image`);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [image]);

    const screens = useBreakpoint();

    const { url } = getConfig().publicRuntimeConfig;

    const companies = gallery.companies.filter((company: Company): boolean => company.premium);

    const { skyscraper, wideboardBottom, halfpage, rectangle } = ads;
    const currentPicture = gallery.images[image];
    return (
        <>
            <Row>
                <Col span={24}>
                    <SectionTitle title="Zdjęcia" url="/galerie" noArrow />
                </Col>
                <Col span={24}>
                    <Title>{gallery.title}</Title>
                </Col>
            </Row>
            <Row justify="center" id="image">
                <Col>
                    <SliderImage
                        onClick={sendShowPhotoEvent}
                        src={resolveImagePath(currentPicture, 'original')}
                        preview={{
                            visible: previewVisible,
                            src: resolveImagePath(currentPicture, 'original'),
                            onVisibleChange: (visible) => {
                                setPreviewVisible(visible);
                            },
                            toolbarRender: (
                                _,
                                { transform: { scale }, actions: { onZoomOut, onZoomIn } }
                            ) => (
                                <ToolbarWrapper>
                                    <Space size={12} className="toolbar-wrapper">
                                        <ZoomOutOutlined
                                            disabled={scale === 1}
                                            onClick={onZoomOut}
                                            style={{ fontSize: 24 }}
                                        />
                                        <ZoomInOutlined
                                            disabled={scale === 50}
                                            onClick={onZoomIn}
                                            style={{ fontSize: 24 }}
                                        />
                                        <PhotoButton
                                            href={`/galerie/${gallery.seoSlug},${image - 1}`}
                                            $disabled={image === 0}
                                        >
                                            <ArrowLeftOutlined style={{ fontSize: 24 }} />
                                        </PhotoButton>
                                        <PhotoButton
                                            href={`/galerie/${gallery.seoSlug},${image + 1}`}
                                            $disabled={image + 1 >= gallery.images.length}
                                        >
                                            <ArrowRightOutlined style={{ fontSize: 24 }} />
                                        </PhotoButton>
                                    </Space>
                                </ToolbarWrapper>
                            )
                        }}
                        alt={`carousel-image-${currentPicture}`}
                    />
                </Col>
                <Col span={24}>
                    <StyledDivider orientation="right">
                        <ButtonGroup
                            style={{
                                background: 'white'
                            }}
                        >
                            <PaginationButton
                                href={`/galerie/${gallery.seoSlug},${image - 1}#image`}
                                title="Pokaż poprzednie zdjęcie"
                                $disabled={image === 0}
                            >
                                <LeftCircleOutlinedIcon style={{ fontSize: 26 }} />
                            </PaginationButton>
                            <PaginationButton
                                href={`/galerie/${gallery.seoSlug},${image + 1}#image`}
                                $disabled={image + 1 >= gallery.images.length}
                                title="Pokaż kolejne zdjęcie"
                            >
                                <RightCircleOutlinedIcon style={{ fontSize: 26 }} />
                            </PaginationButton>
                        </ButtonGroup>
                    </StyledDivider>
                </Col>
            </Row>
            <Row>
                <Col span={24}>
                    <Row>
                        <Col xs={24} md={12}>
                            <SkyscraperMobile id={skyscraper[0]} margin="0 auto" />
                        </Col>
                        <Col xs={24} md={12}>
                            <SkyscraperMobile id={skyscraper[1]} margin="35px auto 35px auto" />
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={12} md={12}>
                            <Date>
                                <FormattedDate date={gallery.dateDisplayed} />
                            </Date>
                        </Col>
                        <Col xs={12} md={8}>
                            <Row justify="end">
                                <Col>
                                    <ShareButtons
                                        title={gallery.seoTitle || gallery.title}
                                        teaser={gallery.seoDescription || gallery.description}
                                        url={`${url}/galerie/${gallery.seoSlug}`}
                                    />
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </Col>
                <Content>
                    <Description dangerouslySetInnerHTML={{ __html: gallery.description }} />
                    {screens.md && (
                        <Row gutter={[8, 8]}>
                            {gallery.images.map((image, i) => (
                                <Col md={8} key={`images-${image}-${i}`}>
                                    <Link
                                        href={{
                                            pathname: `/galerie/${gallery.seoSlug},${i}`
                                        }}
                                        title="Pokaż zdjęcie"
                                    >
                                        <Photo
                                            alt={`gallery photo no ${i}`}
                                            $active={false}
                                            type="index_medium"
                                            height={159}
                                            width={281}
                                            src={image}
                                            style={{
                                                maxWidth: '100%',
                                                objectFit: 'cover'
                                            }}
                                        />
                                    </Link>
                                </Col>
                            ))}
                        </Row>
                    )}
                    {companies && companies.length > 0 && (
                        <Section>
                            <Col span={24}>
                                <SectionTitle title="Powiązane firmy" />
                            </Col>
                            <Col span={24}>
                                <CompanyCarousel companies={companies} />
                            </Col>
                        </Section>
                    )}
                </Content>
                <Sider>
                    <StyledSection gutter={[16, 16]}>
                        {gallery.tags && gallery.tags.length > 0 && (
                            <Col span={24}>
                                <Row>
                                    <Col span={24}>
                                        <Divider />
                                    </Col>
                                    <Col span={24}>
                                        <SectionTitle title="Powiązane tagi" />
                                    </Col>
                                    <Col span={24}>
                                        <Col span={24}>
                                            {gallery.tags && <Tags tags={gallery.tags} />}
                                        </Col>
                                    </Col>
                                    <Col span={24}>
                                        <Divider />
                                    </Col>
                                </Row>
                            </Col>
                        )}
                        <Col span={24}>
                            <HalfPage id={halfpage[0]} />
                        </Col>
                    </StyledSection>
                </Sider>

                <Col span={24}>
                    <Wideboard id={wideboardBottom[1]} />
                </Col>
                <Col span={24}>
                    <Section gutter={16}>
                        <Col xs={24} md={18}>
                            <SectionTitle title="Zobacz inne galerie" url="/galerie" />
                            <GalleriesCarousel galleries={galleries} itemsPerRow={3} />
                        </Col>
                        <Col xs={24} md={6}>
                            <SectionTitle title="Wideo" url="/wideo" />
                            <VideoCard video={singleVideo} />
                        </Col>
                    </Section>
                </Col>
                <Col span={24}>
                    <Row gutter={16}>
                        <Content>
                            <Newsletter />
                            <Divider />
                        </Content>
                        <Sider>
                            <Row>
                                <Col span={24}>
                                    <Rectangle id={rectangle[0]} />
                                </Col>
                            </Row>
                        </Sider>
                    </Row>
                </Col>
                <Col span={24}>
                    <Subscription issue={latestIssue} />
                </Col>
                <Col span={24}>
                    <Section>
                        <Col span={24}>
                            <Wideboard id={wideboardBottom[0]} />
                        </Col>
                    </Section>
                </Col>
            </Row>
        </>
    );
};

export default GalleryDetails;
